import {useSelector} from 'react-redux'
import {useOfferContainer} from 'hooks/useOfferContainer'
import {
  getClickedBundleId,
  getClickedOfferId
} from 'modules/accommodation/utils'
import {OldHotelOfferEntity} from 'modules/sapiSearch/offersMapping'
import {
  getFilteredRoomsData,
  getIsRoomsPollingComplete
} from 'modules/searchApi/selectors'

interface Props {
  offerEntity: OldHotelOfferEntity
  hotelId: string
  withGreatDealTag?: boolean
}

/**
 * AP-only wrapper around useOfferContainer. A clickedOffer or clickedBundle in
 * the URL means the customer already picked a Vio offer on Search, and the
 * rooms response is the source of truth for it: top-offers only carries a
 * ranked slice (NEXT_PUBLIC_VISIBLE_TOP_OFFERS_LIMIT) of the offer entity and a
 * bundle isn't in there at all. So a selection pins the primary offer to the
 * rooms data - including while it's still on its way, to not show a top-offers
 * price that the rooms one then replaces.
 */
export const useAccommodationPrimaryOffer = ({
  offerEntity,
  hotelId,
  withGreatDealTag
}: Props) => {
  const clickedOfferId = getClickedOfferId()
  const offerContainer = useOfferContainer({
    offerEntity,
    offerId: clickedOfferId,
    hotelId,
    withGreatDealTag
  })
  const {rooms, splitBooking} = useSelector(getFilteredRoomsData)
  const isRoomsPollingComplete = useSelector(getIsRoomsPollingComplete)

  const hasSelectedOfferOrBundle = Boolean(
    clickedOfferId || getClickedBundleId()
  )
  const hasRoomOffers = rooms.length > 0 || Boolean(splitBooking)
  // Rooms are still on their way until polling completes - keep the bar up in
  // its loading state, and only drop it once they came back with nothing.
  const shouldRenderPrimaryRoomOffer =
    hasSelectedOfferOrBundle && (hasRoomOffers || !isRoomsPollingComplete)

  return {
    ...offerContainer,
    hasSelectedOfferOrBundle,
    shouldRenderPrimaryRoomOffer
  }
}
